import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpHandler,
  HttpEvent,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { HttpUtils } from '../utils/http.utils';
import { AppConfigService } from '../services/app-config.service';
import { ToastService } from '../services/toast.service';
import { AuthService } from '../services/auth.service';
import { TranslationService } from '@services/translation.service';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  constructor(
    private appConfig: AppConfigService,
    private authService: AuthService,
    private toastService: ToastService,
    private ts: TranslationService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let observable: Observable<HttpRequest<any>>;

    const url = this.handleUrl(req.url);

    if (this.appConfig.isApiUrl(url)) {
      observable = this.authService.getToken().pipe(
        map((token) => {
          if (token) {
            req = req.clone({
              url,
              setHeaders: {
                Authorization: `Bearer ${token}`,
              },
            });
          } else {
            req = req.clone({
              url,
            });
          }

          return req;
        })
      );
    } else {
      observable = this.isArcGisUrl(url).pipe(
        mergeMap((aBool) => {
          let tokenObservable: Observable<string>;
          if (aBool) {
            tokenObservable = this.authService.getEsriToken();
          } else {
            tokenObservable = of(null);
          }

          return tokenObservable.pipe(
            map((token) => {
              if (token) {
                req = req.clone({
                  url,
                  setParams: { token },
                });
              } else {
                req = req.clone({
                  url,
                });
              }

              return req;
            })
          );
        })
      );
    }

    return observable.pipe(
      mergeMap((req) => {
        return next.handle(req);
      }),
      mergeMap((response) => {
        if (
          response instanceof HttpResponse &&
          response.body &&
          response.body.error
        ) {
          return throwError(HttpUtils.createBadRequest());
        }
        return of(response);
      }),
      catchError((response) => {
        if (response instanceof HttpErrorResponse) {
          switch (response.status) {
            case 0:
              this.toastService.error(
                this.ts.getCurrentLanguage()['unable-access-data']
              );
              response = false;
              break;
            case HttpStatusCode.Unauthorized:
              this.toastService.error(
                this.ts.getCurrentLanguage()['you-are-not-authenticated']
              );
              response = false;
              break;
          }
        }

        return throwError(response);
      })
    );
  }

  handleUrl(url: string) {
    return this.appConfig.normalizeUrl(url);
  }

  isArcGisUrl(url: string) {
    return of(this.appConfig.serverUrl).pipe(
      map((baseUrl) => {
        return url.startsWith(baseUrl);
      })
    );
  }
}
